import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDateTime, formatNumber } from '@/lib/format'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Pagination } from '@/components/Pagination'
import { EmptyState } from '@/components/EmptyState'
import { SYNC_FROM_SEGMENT_PATH } from '@/components/HubSpotSyncDialog'
import { Download, Info, Layers, Search } from 'lucide-react'
import { useSegments } from '@/hooks/useApi'
import { useDebounce } from '@/hooks/useDebounce'

export function Segments() {
  const { t } = useTranslation('contacts')
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 300)
  const { data: segments, isLoading } = useSegments(page, 50, debouncedSearch)

  const formatDate = (iso: string | null) => {
    if (!iso) return t('segments.neverSynced')
    return formatDateTime(iso, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">{t('segments.title')}</h1>
      </div>

      <section
        aria-labelledby="segments-info-title"
        className="mb-6 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 p-4"
      >
        <div className="flex items-start gap-3 text-blue-900 dark:text-blue-200">
          <Info className="h-4 w-4 mt-1 shrink-0" aria-hidden="true" />
          <div className="space-y-1 text-sm">
            <h2 id="segments-info-title" className="font-medium">
              {t('segments.info.title')}
            </h2>
            <p>{t('segments.info.whatAre')}</p>
            <p>{t('segments.info.source')}</p>
            <p>{t('segments.info.usage')}</p>
            <p>{t('segments.info.import')}</p>
            <Button asChild variant="outline" size="sm" className="mt-2 bg-background">
              <Link to={SYNC_FROM_SEGMENT_PATH}>
                <Download className="h-4 w-4 mr-2" aria-hidden="true" />
                {t('segments.importList')}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle>{t('segments.yourSegments')}</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('segments.searchPlaceholder')}
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value)
                  setPage(1)
                }}
                className="pl-9 w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : segments && segments.items.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('segments.table.name')}</TableHead>
                    <TableHead className="text-right">{t('segments.table.contacts')}</TableHead>
                    <TableHead>{t('segments.table.lastSync')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {segments.items.map((seg) => (
                    <TableRow
                      key={seg.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/segments/${seg.id}`)}
                    >
                      <TableCell className="font-medium">{seg.name}</TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">
                        {formatNumber(seg.contact_count)}
                        <span className="text-muted-foreground">
                          {' '}
                          {t('segments.syncedOfTotal', { total: formatNumber(seg.hubspot_size) })}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(seg.last_synced_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                page={page}
                totalPages={segments.pages}
                onPageChange={setPage}
              />
            </>
          ) : (
            <EmptyState
              icon={Layers}
              title={debouncedSearch ? t('segments.searchEmpty.title') : t('segments.empty.title')}
              description={
                debouncedSearch
                  ? t('segments.searchEmpty.description')
                  : t('segments.empty.description')
              }
              action={
                debouncedSearch
                  ? undefined
                  : {
                      label: t('segments.importList'),
                      onClick: () => navigate(SYNC_FROM_SEGMENT_PATH),
                    }
              }
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
